import { createContext, useContext, useReducer } from 'react';
import { Palette } from './Palette';
import { ALL_COMPONENT_TYPES, type Component } from './ComponentsPreview';
import type { ComponentProperty } from '@/services/ConversationService';
import { Column, Font, Row } from './DesignSystem';

type ComponentPreviewDebugUnreadPropertyIssues = Record<
  Component['type'],
  {
    propertyNames: string[];
  }
>;

type ComponentPreviewDebugIssues = {
  empty: boolean;
  unreadPropertyIssues: ComponentPreviewDebugUnreadPropertyIssues;
};

type ComponentPreviewDebugReducerState = {
  issues: ComponentPreviewDebugIssues;
};
type ComponentPreviewDebugReducerAction =
  | {
      type: 'clear-issues';
    }
  | {
      type: 'mark-unread-properties';
      componentType: Component['type'];
      propertyNames: string[];
    };
type ComponentPreviewDebugReducerDispatch = (
  _: ComponentPreviewDebugReducerAction,
) => void;
type ComponentPreviewDebugContextProps = {
  state: ComponentPreviewDebugReducerState;
  dispatch: ComponentPreviewDebugReducerDispatch;
};

const ComponentPreviewDebugContext =
  createContext<ComponentPreviewDebugContextProps | null>(null);

export function useComponentPreviewDebug(): ComponentPreviewDebugContextProps {
  const context = useContext(ComponentPreviewDebugContext);
  if (context === null) {
    throw new Error(
      'useComponentPreviewDebug must be used within a ComponentPreviewDebugBoundary',
    );
  }

  return context;
}

export function ComponentPreviewDebugBoundary({
  children,
}: React.PropsWithChildren) {
  const InitialIssues: ComponentPreviewDebugIssues = {
    empty: true,
    unreadPropertyIssues: {
      function: {
        propertyNames: [],
      },
      label: {
        propertyNames: [],
      },
      button: {
        propertyNames: [],
      },
      textInput: {
        propertyNames: [],
      },
      binaryToggle: {
        propertyNames: [],
      },
      image: {
        propertyNames: [],
      },
      segmentedControl: {
        propertyNames: [],
      },
      list: {
        propertyNames: [],
      },
      tabBar: {
        propertyNames: [],
      },
      stack: {
        propertyNames: [],
      },
      group: {
        propertyNames: [],
      },
    },
  };
  function reducer(
    state: ComponentPreviewDebugReducerState,
    action: ComponentPreviewDebugReducerAction,
  ): ComponentPreviewDebugReducerState {
    switch (action.type) {
      case 'clear-issues': {
        return {
          ...state,
          issues: InitialIssues,
        };
      }
      case 'mark-unread-properties': {
        if (action.propertyNames.length === 0) {
          // no-op
          return state;
        }

        const existingUnreadPropertyIssues =
          state.issues.unreadPropertyIssues[action.componentType];

        const unreadPropertyNames = existingUnreadPropertyIssues.propertyNames;
        for (const unreadPropertyName of action.propertyNames) {
          if (unreadPropertyNames.includes(unreadPropertyName)) {
            continue;
          }
          unreadPropertyNames.push(unreadPropertyName);
        }

        return {
          ...state,
          issues: {
            ...state.issues,
            empty: false,
            unreadPropertyIssues: {
              ...state.issues.unreadPropertyIssues,
              [action.componentType]: {
                ...existingUnreadPropertyIssues,
                propertyNames: unreadPropertyNames,
              },
            },
          },
        };
      }
    }
  }
  const [state, dispatch] = useReducer(reducer, {
    issues: InitialIssues,
  });
  return (
    <ComponentPreviewDebugContext.Provider value={{ state, dispatch }}>
      {children}
    </ComponentPreviewDebugContext.Provider>
  );
}

export function ComponentPreviewDebugPalette({}) {
  const { state } = useComponentPreviewDebug();
  const noIssues = state.issues.empty;
  return (
    <Palette title="Preview debug" windowID="component-preview-debug">
      <Column>
        {noIssues && (
          <Font kind="font-body">
            No issues when rendering the Component Preview!
          </Font>
        )}
        {ALL_COMPONENT_TYPES.map((componentType) => {
          const unreadPropertyNames =
            state.issues.unreadPropertyIssues[componentType].propertyNames;
          if (unreadPropertyNames.length === 0) {
            return undefined;
          }
          return (
            <Row>
              <Font kind="font-body">
                When rendering: {componentType} the following properties were
                not read: {unreadPropertyNames.join(', ')}
              </Font>
            </Row>
          );
        })}
      </Column>
    </Palette>
  );
}
